import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { scenarioDataV12, selectorCopy, defaultScenarioId } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];

export const FamilySelectorV12 = () => {
  const [activeId, setActiveId] = useState(defaultScenarioId);
  const active = scenarioDataV12.find((s) => s.id === activeId);
  const light = active.tone === "light";

  return (
    <section
      data-testid="family-selector-section"
      className="px-6 lg:px-10 py-14 lg:py-20"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="selector-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--strollee-accent)]"
          >
            {selectorCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] lg:text-[48px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-ink)]">
            {selectorCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[var(--strollee-muted)]">
          {selectorCopy.support}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.12 }}
        className="mt-8 lg:mt-10"
      >
        <div className={`relative h-[calc(90vh-76px)] lg:h-[calc(90vh-80px)] overflow-hidden rounded-[var(--strollee-radius-hero)] transition-colors duration-500 ${light ? "bg-[var(--strollee-surface)]" : "bg-[var(--strollee-line)]/40"}`}>
          {scenarioDataV12.map((s) => (
            <motion.img
              key={s.id}
              src={s.image.url}
              alt={s.image.alt}
              data-testid={s.id === activeId ? "scenario-media-active" : `scenario-media-${s.id}`}
              initial={false}
              animate={{
                opacity: s.id === activeId ? 1 : 0,
                scale: s.id === activeId ? 1 : 1.03,
              }}
              transition={{ duration: 0.7, ease: EASE }}
              style={s.image.position ? { objectPosition: s.image.position } : undefined}
              className={`absolute inset-0 h-full w-full ${
                s.image.fit === "contain" ? "object-contain p-8 lg:p-12" : "object-cover"
              }`}
            />
          ))}
          <div
            className={`absolute inset-0 transition-colors duration-500 ${
              light
                ? "bg-transparent"
                : "bg-gradient-to-t from-[#171713]/70 via-[#171713]/10 to-[#171713]/25"
            }`}
          />

          <div
            role="tablist"
            aria-label="Family scenarios"
            data-testid="scenario-tablist"
            className={`absolute left-6 top-6 lg:left-8 lg:top-8 inline-flex items-center gap-1 rounded-full border p-1.5 backdrop-blur-md transition-colors duration-500 ${
              light
                ? "border-[var(--strollee-line)] bg-white/75 shadow-[0_2px_16px_rgba(23,23,19,0.08)]"
                : "border-white/25 bg-white/10"
            }`}
          >
            {scenarioDataV12.map((s) => {
              const isActive = s.id === activeId;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`scenario-tab-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`relative rounded-full px-5 py-2.5 text-[13px] transition-colors duration-200 ${
                    isActive
                      ? light
                        ? "font-semibold text-[var(--strollee-surface)]"
                        : "font-semibold text-[var(--strollee-ink)]"
                      : light
                        ? "font-medium text-[var(--strollee-muted)] hover:text-[var(--strollee-ink)]"
                        : "font-medium text-white/75 hover:text-white"
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="scenario-active-pill-v12"
                      transition={{ duration: 0.4, ease: EASE }}
                      className={`absolute inset-0 rounded-full ${
                        light ? "bg-[var(--strollee-ink)]" : "bg-[var(--strollee-surface)]"
                      }`}
                    />
                  )}
                  <span className="relative z-10">{s.label}</span>
                </button>
              );
            })}
          </div>

          <div className="absolute inset-x-0 bottom-0 p-8 lg:p-14">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="max-w-[520px]"
              >
                <h3
                  data-testid="scenario-title"
                  className={`text-[32px] lg:text-[44px] font-semibold leading-[1.08] tracking-[-0.025em] transition-colors duration-500 ${
                    light ? "text-[var(--strollee-ink)]" : "text-white"
                  }`}
                >
                  {active.title}
                </h3>
                <p
                  data-testid="scenario-copy"
                  className={`mt-3 max-w-[440px] text-base lg:text-lg leading-relaxed transition-colors duration-500 ${
                    light ? "text-[var(--strollee-ink)]/70" : "text-white/80"
                  }`}
                >
                  {active.copy}
                </p>
                <div className="mt-7 flex items-center gap-6">
                  <a
                    href="#setup"
                    data-testid="scenario-explore-cta"
                    className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold tracking-wide transition-all duration-200 hover:-translate-y-[2px] ${
                      light
                        ? "bg-[var(--strollee-ink)] text-[var(--strollee-surface)] hover:shadow-[0_8px_28px_rgba(23,23,19,0.25)]"
                        : "bg-[var(--strollee-surface)] text-[var(--strollee-ink)] hover:bg-white hover:shadow-[0_8px_28px_rgba(0,0,0,0.25)]"
                    }`}
                  >
                    {selectorCopy.exploreCta}
                    <ArrowUpRight
                      size={16}
                      strokeWidth={2.2}
                      className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
                    />
                  </a>
                  <a
                    href="#find-my-setup"
                    data-testid="find-my-setup-link"
                    className={`group inline-flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 ${
                      light
                        ? "text-[var(--strollee-ink)]/60 hover:text-[var(--strollee-ink)]"
                        : "text-white/70 hover:text-white"
                    }`}
                  >
                    {selectorCopy.quizHint}{" "}
                    <span className="font-semibold underline-offset-4 group-hover:underline">
                      {selectorCopy.quizLink}
                    </span>
                    <ArrowRight
                      size={14}
                      strokeWidth={2.4}
                      className="transition-transform duration-200 group-hover:translate-x-[2px]"
                    />
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
